import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Tooltip, type SxProps, type Theme } from '@mui/material';

/**
 * Cores dos gráficos por papel. "Você" é a cor da marca (violeta no claro, lima no escuro); a turma fica num
 * neutro recessivo, para o aluno se achar no gráfico de relance. As três fatias da composição da P1 usam os
 * três primeiros tons da paleta categórica validada (azul, laranja, água), passos próprios para o escuro.
 */
export const COR = {
  voce: 'var(--g-voce)',
  turma: 'var(--g-turma)',
  turmaForte: 'var(--g-turma-forte)',
  trilho: 'var(--g-trilho)',
  grade: 'var(--g-grade)',
  superficie: 'var(--g-superficie)',
  texto: 'var(--g-texto)',
  textoFraco: 'var(--g-texto-fraco)',
  serie: ['var(--g-s1)', 'var(--g-s2)', 'var(--g-s3)'],
};

const variaveis: SxProps<Theme> = (theme) => ({
  '--g-voce': theme.vars.palette.primary.main,
  '--g-turma': '#C3BBD6',
  '--g-turma-forte': '#6E6687',
  '--g-trilho': '#ECE8F4',
  '--g-grade': '#DDD7EB',
  '--g-superficie': theme.vars.palette.background.paper,
  '--g-texto': theme.vars.palette.text.primary,
  '--g-texto-fraco': theme.vars.palette.text.secondary,
  '--g-s1': '#2a78d6',
  '--g-s2': '#eb6834',
  '--g-s3': '#1baf7a',
  ...theme.applyStyles('dark', {
    '--g-turma': '#524A6B',
    '--g-turma-forte': '#9D95B3',
    '--g-trilho': '#2A2146',
    '--g-grade': '#3B3158',
    '--g-s1': '#3987e5',
    '--g-s2': '#d95926',
    '--g-s3': '#199e70',
  }),
});

/** Mede a largura disponível para desenhar o SVG em pixels reais (texto sem distorção). */
export function useLargura<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [largura, setLargura] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setLargura(Math.floor(e!.contentRect.width)));
    obs.observe(el);
    setLargura(Math.floor(el.getBoundingClientRect().width));
    return () => obs.disconnect();
  }, []);
  return { ref, largura };
}

export function AreaGrafico({
  altura,
  rotulo,
  children,
  sx,
}: {
  altura: number;
  rotulo: string;
  children: (largura: number) => ReactNode;
  sx?: SxProps<Theme>;
}) {
  const { ref, largura } = useLargura<HTMLDivElement>();
  return (
    <Box ref={ref} sx={[variaveis, { width: '100%', minHeight: altura }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {largura > 0 && (
        <svg width={largura} height={altura} role="img" aria-label={rotulo} style={{ display: 'block', overflow: 'visible' }}>
          {children(largura)}
        </svg>
      )}
    </Box>
  );
}

/** Retângulo com cantos arredondados só no topo (ou só à direita, na horizontal). */
export function barraPath(x: number, y: number, w: number, h: number, r: number, horizontal = false) {
  if (w <= 0 || h <= 0) return '';
  const rr = Math.min(r, horizontal ? h / 2 : w / 2, horizontal ? w : h);
  if (horizontal) {
    return `M${x},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h - rr} Q${x + w},${y + h} ${x + w - rr},${y + h} H${x} Z`;
  }
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

/** Área de hover maior que a marca, com tooltip do MUI. */
export function Alvo({ titulo, children }: { titulo: ReactNode; children: React.ReactElement }) {
  return (
    <Tooltip title={titulo} followCursor arrow={false} enterTouchDelay={0}>
      {children}
    </Tooltip>
  );
}

export const VARIAVEIS_GRAFICO = variaveis;
